// src/modules/app-views/products/pages/Ai/helpers/index.js
import translateX from 'utils/translateX';

/**
 * Build user message content from form values
 */
export function buildUserMessageContent(values) {
  const { productDescription, price, supplierName, ean } = values;
  let content = `• ${productDescription}`;
  if (price) content += `\n• ${translateX('price')}: ${formatPrice(price)}`;
  if (supplierName) content += `\n• ${translateX('supplier')}: ${supplierName}`;
  if (ean) content += `\n• EAN: ${ean}`;
  return content;
}

/**
 * Format price to BRL currency
 */
export function formatPrice(value, showZeroIfEmpty = false) {
  // Handle null, undefined, NaN, or invalid values
  if (value === null || value === undefined || isNaN(value)) {
    if (showZeroIfEmpty) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(0);
    }
    return '';
  }
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/**
 * Format price compact (always shows value)
 */
export function formatPriceCompact(value) {
  if (value === null || value === undefined || isNaN(value)) {
    return 'R$ 0,00';
  }
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Build natural language intro for cataloged product
 */
export function buildNaturalIntro(product, alreadyExists, resolvedSupplier) {
  const name = product?.shortDescription || product?.name || '';
  const brand = product?.classification?.brand;
  const category = product?.classification?.category;

  // Build a natural language intro
  let intro = '';

  if (alreadyExists) {
    intro = translateX('ai_intro_already_exists', { name });
  } else {
    intro = translateX('ai_intro_found_product', { name });
  }

  // Add brand if available
  if (brand) {
    intro += ' ' + translateX('ai_intro_brand', { brand });
  }

  // Add category if available
  if (category) {
    intro += ' ' + translateX('ai_intro_category', { category });
  }

  // Add supplier if resolved
  if (resolvedSupplier?.name) {
    intro += ' ' + translateX('ai_intro_supplier', { supplier: resolvedSupplier.name });
  }

  return intro;
}

/**
 * Build error details from API response
 */
export function buildErrorDetails({ message, code, details, statusCode, isNetworkError }) {
  // Store translation KEYS instead of translated strings
  // This allows the UI to re-translate when language changes
  const result = {
    explanationKey: '',
    explanationFallback: '', // Used when explanation is the raw message itself
    technicalMessage: message || '',
    suggestionKeys: [],
  };

  // Network errors
  if (isNetworkError) {
    result.explanationKey = 'error_network_connection';
    result.suggestionKeys = ['suggestion_check_internet', 'suggestion_try_again_later'];
    return result;
  }

  // Handle specific error codes
  if (code) {
    switch (code) {
      case 'AI_SERVICE_UNAVAILABLE':
        result.explanationKey = 'error_ai_service_unavailable';
        result.suggestionKeys = ['suggestion_try_different_model', 'suggestion_try_again_later'];
        result.technicalMessage = '';
        break;
      case 'AI_RATE_LIMIT':
        result.explanationKey = 'error_ai_rate_limit';
        result.suggestionKeys = ['suggestion_wait_few_minutes', 'suggestion_try_different_model'];
        break;
      case 'AI_CONTEXT_TOO_LONG':
        result.explanationKey = 'error_ai_context_too_long';
        result.suggestionKeys = ['suggestion_simplify_description'];
        break;
      case 'AI_INVALID_RESPONSE':
        result.explanationKey = 'error_ai_invalid_response';
        result.suggestionKeys = ['suggestion_try_again', 'suggestion_try_different_model'];
        break;
      case 'PRODUCT_VALIDATION_ERROR':
        result.explanationKey = 'error_product_validation';
        result.technicalMessage = details || message;
        result.suggestionKeys = ['suggestion_check_product_data', 'suggestion_verify_price'];
        break;
      case 'SUPPLIER_NOT_FOUND':
        result.explanationKey = 'error_supplier_not_found';
        result.suggestionKeys = [
          'suggestion_check_supplier_name',
          'suggestion_leave_supplier_empty',
        ];
        break;
      case 'INSUFFICIENT_CREDITS':
        result.explanationKey = 'error_insufficient_credits';
        result.suggestionKeys = ['suggestion_add_credits', 'suggestion_contact_support'];
        break;
      default:
        // Generic error with code
        result.explanationKey = 'error_processing_request';
        result.technicalMessage = `[${code}] ${message || ''}`;
        result.suggestionKeys = ['suggestion_try_again', 'suggestion_contact_support_if_persists'];
    }
    return result;
  }

  // Handle HTTP status codes
  if (statusCode) {
    switch (statusCode) {
      case 400:
        result.explanationKey = 'error_invalid_request';
        result.suggestionKeys = [
          'suggestion_check_product_data',
          'suggestion_verify_required_fields',
        ];
        break;
      case 401:
      case 403:
        result.explanationKey = 'error_authentication';
        result.suggestionKeys = ['suggestion_refresh_page', 'suggestion_login_again'];
        break;
      case 404:
        result.explanationKey = 'error_service_not_found';
        result.suggestionKeys = ['suggestion_contact_support'];
        break;
      case 429:
        result.explanationKey = 'error_too_many_requests';
        result.suggestionKeys = ['suggestion_wait_few_minutes'];
        break;
      case 500:
      case 502:
      case 503:
        result.explanationKey = 'error_server_error';
        result.suggestionKeys = [
          'suggestion_try_again_later',
          'suggestion_contact_support_if_persists',
        ];
        break;
      default:
        result.explanationKey = 'error_unexpected';
        result.suggestionKeys = ['suggestion_try_again', 'suggestion_contact_support_if_persists'];
    }
    return result;
  }

  // Fallback: try to interpret the message
  if (message) {
    const lowerMessage = message.toLowerCase();

    if (lowerMessage.includes('timeout') || lowerMessage.includes('tempo')) {
      result.explanationKey = 'error_timeout';
      result.suggestionKeys = ['suggestion_simplify_description', 'suggestion_try_again'];
    } else if (
      lowerMessage.includes('price') ||
      lowerMessage.includes('preco') ||
      lowerMessage.includes('preço')
    ) {
      result.explanationKey = 'error_price_issue';
      result.suggestionKeys = ['suggestion_verify_price'];
    } else if (
      lowerMessage.includes('description') ||
      lowerMessage.includes('descricao') ||
      lowerMessage.includes('descrição')
    ) {
      result.explanationKey = 'error_description_issue';
      result.suggestionKeys = ['suggestion_improve_description'];
    } else {
      // Use the raw message as fallback (no translation key)
      result.explanationFallback = message;
      result.suggestionKeys = ['suggestion_try_again', 'suggestion_contact_support_if_persists'];
    }
    return result;
  }

  // Ultimate fallback
  result.explanationKey = 'error_unexpected';
  result.suggestionKeys = ['suggestion_try_again', 'suggestion_contact_support'];

  return result;
}
